import React, { useCallback, useState } from 'react';
import { Grid } from '@material-ui/core';
import { ComponentController, PickerComponent, ConfirmationDialog, BannerType } from 'components/common';
import { PayeeFormNames, OrganisationDataSources, CountriesWithStatesIso, PayeeTypes } from './Helpers';
import { Datasource } from 'Api';
import { getMdmCompanyByOrbisId } from './CreatePayee.api';

const PayeeLookup = ({
  payeeDetailsState, setPayeeDetailsState, abortController, setAbortController,
  control, forceClearDatasourceState, setForceClearDatasourceState, errors, getRequiredError, setBannerState,
  setValue, setLoading, renderLookupHeader, renderLookupView, Popup
}) => {
  const [sanctionWarningDialogState, setSanctionWarningDialogState] = useState(false);

  const onPayeeChange = useCallback((value) => {
    setPayeeDetailsState((previous) => {
      return {
        ...previous,
        payee: value?.organizationName ?? '',
        payeeDetails: value,
        orbisId: value?.orbisId ?? '',
        hamiltonId: value?.hamiltonId ?? '',
        source: value?.source ?? '',
        city: value?.city ?? '',
        addressLine1: value?.addressLine1,
        addressLine2: value?.addressLine2,
        addressLine3: value?.addressLine3,
        ...(value && {
          dateOfBirth: value.dateOfBirth ?? null,
          address: value.address ?? '',
          postCode: value.postcode ?? ''
        }),
      };
    });
    abortController.abort();
  }, [setPayeeDetailsState, abortController]);

  const handleSanctionCheck = useCallback((passfortSanctionsFlag, selectedPayee) => {
    const isSanctioned = passfortSanctionsFlag?.toLowerCase() === "true";

    if (isSanctioned) {
      setSanctionWarningDialogState(true);
      setPayeeDetailsState((previous) => {
        return ({
          ...previous,
          payeeDetails: selectedPayee
        });
      });
    } else {
      onPayeeChange(selectedPayee);
    }
  }, [setSanctionWarningDialogState, setPayeeDetailsState, onPayeeChange]);

  const checkForSanctionFlag = useCallback(async (selectedPayee) => {
    if (selectedPayee?.source === OrganisationDataSources.Orbis) {
      setLoading(true);
      let response = await getMdmCompanyByOrbisId(selectedPayee.orbisId);
      setLoading(false);

      if (response.success) {
        let payeeResponse = response?.data;
        selectedPayee.hamiltonId = payeeResponse?.hamiltonId?.toString() ?? "";;
        handleSanctionCheck(payeeResponse?.sanctionsFlag, selectedPayee);
      }
      else {
        setBannerState({
          type: BannerType.error,
          message: response.errorMessage,
          show: true,
          expose: true
        });
      }
    }
    else if (selectedPayee?.source === OrganisationDataSources.MDM) {
      handleSanctionCheck(selectedPayee?.sanctionsFlag, selectedPayee);
    }
    else {
      onPayeeChange(selectedPayee);
    }
  }, [setLoading, handleSanctionCheck, onPayeeChange]);

  const renderPayeeLookup = () => {
    const datasource = Datasource.organisations.payee;
    const isUSorCA = CountriesWithStatesIso.includes(payeeDetailsState.countryOfIncorporationIso2) || CountriesWithStatesIso.includes(payeeDetailsState.countryOfResidencyIso2);
    const hasPayeeType = !!payeeDetailsState.payeeType;
    const hasCountry = !!(payeeDetailsState.countryOfIncorporationIso2 || payeeDetailsState.countryOfResidencyIso2);
    const hasState = !isUSorCA || !!(payeeDetailsState.stateOfIncorporationCode || payeeDetailsState.stateOfResidencyCode);
    const isCompany = PayeeTypes.company === payeeDetailsState.payeeType;
    const stateIso = isCompany ? payeeDetailsState.stateOfIncorporationCode : payeeDetailsState.stateOfResidencyCode;
    const disabled = !(hasPayeeType && hasCountry && hasState);

    const name = "payee";
    return payeeDetailsState.payeeType && (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          required={true}
          render={({ field: { name, onBlur, onChange } }) =>
            <PickerComponent
              label={PayeeFormNames[name]}
              fieldName={name}
              value={payeeDetailsState[name]}
              datasource={datasource}
              config={{
                newValueAtTheEnd: true,
                rowsLimit: 100
              }}
              requestData={{
                orgType: payeeDetailsState.payeeType,
                countryOfIncorporationIso2: payeeDetailsState.countryOfIncorporationIso2,
                countryOfResidencyIso2: payeeDetailsState.countryOfResidencyIso2,
                entityDOB: payeeDetailsState.dateOfBirth ?? null,
                address: isCompany ? '': payeeDetailsState.address,
                postCode: isCompany ? '': payeeDetailsState.postCode,
                stateIso: stateIso
              }}
              forceClearDatasource={forceClearDatasourceState}
              disabled={disabled}
              abortController={abortController}
              error={!!errors[name]}
              errorText={getRequiredError(name, PayeeFormNames[name])}
              setBannerState={setBannerState}
              setValidationValue={setValue}
              setLoading={setLoading}
              setFormState={setPayeeDetailsState}
              setAbortController={setAbortController}
              onChange={value => { checkForSanctionFlag(value); onChange(value); }}
              onBlur={onBlur}
              Popup={Popup}
              OptionsHeader={renderLookupHeader()}
              OptionView={renderLookupView()}
            />
          }
        />
      </Grid>
    );
  }

  const handleAcknowledge = useCallback(() => {
    onPayeeChange(payeeDetailsState?.payeeDetails);
  }, [onPayeeChange, payeeDetailsState?.payeeDetails]);

  const handleClose = useCallback(() => {
    setValue('payee', null);

    setForceClearDatasourceState({
      'payee': true
    });

    onPayeeChange(null);
  }, [setValue, setForceClearDatasourceState, onPayeeChange]);

  const renderSanctionWarning = () => {
    let dialogContent = "Please note that there is known sanctions risk exposure in relation to this payee";
    return (
      <ConfirmationDialog
        isOpened={sanctionWarningDialogState}
        content={dialogContent}
        onConfirm={() => handleAcknowledge()}
        config={{
          cancelButtonLabel: "Cancel",
          confirmButtonLabel: "Acknowledge"
        }}
        onClose={() => handleClose()}
        setIsOpened={setSanctionWarningDialogState}
      />
    );
  }

  return (
    <>
      {renderPayeeLookup()}
      {renderSanctionWarning()}
    </>
  );
}

export default PayeeLookup;